// @flow

import React, { useEffect } from "react";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { Form, Button, Col, Modal, ModalHeader, ModalBody, ModalFooter, Text } from "@components";
import { connect } from "react-redux";
import { TextField } from "@components/form";
import type { State } from "@store/reducers";
import actionCreators from "@modules/reporting/actionCreator";
import { Field, reduxForm, formValueSelector, type FormProps } from "redux-form";
import type {
  SaveReportModalType,
  DefaultReport,
  MySavedReport,
} from "@modules/reporting/reducer/modals/types";
import { getSaveReportModal } from "@modules/reporting/selectors/modals";
import { ReportActionTypesEnums } from "@modules/reporting/constants/reports";

type OwnProps = $Exact<{
  createReport: (data: DefaultReport) => void,
  updateReport: (data: MySavedReport) => void,
}>;

type PropsFromState = $Exact<{
  name: string,
  modal: SaveReportModalType,
}>;

type PropsFromDispatch = $Exact<{
  toggleSaveReportModal: typeof actionCreators.ToggleSaveReportModal,
  toggleAddEditReportModal: typeof actionCreators.ToggleAddEditReportModal,
}>;

type Props = $Exact<{
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...OwnProps,
  ...$Exact<FormProps>, // https://stackoverflow.com/a/44771268/220839
}>;

export function SaveReportModal(props: Props) {
  useEffect(() => {
    if (props.modal.data && props.modal.data.name && props.initialize) {
      props.initialize({
        name: props.modal.data.name,
      });
    }
  }, []);

  function handleCloseModal() {
    props.toggleSaveReportModal({ visible: false });
  }

  function handleSubmit(event: Event) {
    if (event.type === "submit") event.preventDefault();
    if (props.modal.type === ReportActionTypesEnums.COPY) {
      props.createReport({
        name: props.name,
        filters: (props.modal.report || {}).filters,
        columns: (props.modal.report || {}).columns || props.columns,
      });
      return;
    } else if (props.modal.type === ReportActionTypesEnums.RENAME) {
      props.updateReport({
        ...props.modal.report,
        name: props.name,
      });
      return;
    } else {
      props.createReport({
        name: props.name,
        filters: (props.modal.report || {}).filters,
        columns: (props.modal.report || {}).columns || props.columns,
      });
    }

    props.toggleAddEditReportModal({ visible: false });
    props.modal.callback && props.modal.callback();
  }

  const submitButtonName = (props.modal.data || {}).submitButtonName;
  const submitButtonMaterialIcon = (props.modal.data || {}).submitButtonMaterialIcon;

  return (
    <Modal className="modal-small">
      <ModalHeader toggle={handleCloseModal}>{(props.modal.data || {}).modalName}</ModalHeader>
      <ModalBody>
        <Col sm={{ size: 8, offset: 2 }}>
          <Form onSubmit={handleSubmit} className="w-100">
            <Text text={props.modal.data.fieldName} />
            <Field name="name" component={TextField} props={{ value: props.modal.data?.key }} />
          </Form>
        </Col>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-end">
        <Button color="secondary" onClick={handleCloseModal}>
          {props.t("CANCEL")}
        </Button>
        <Button
          onClick={handleSubmit}
          materialIcon={submitButtonMaterialIcon}
          disabled={(props.name && !props.name.trim()) || !props.name}
        >
          {submitButtonName}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

const mapStateToProps = (state: State): PropsFromState => ({
  name: formValueSelector("saveReportModal")(state, "name"),
  modal: getSaveReportModal(state),
});

const mapDispatchToProps = {
  toggleSaveReportModal: actionCreators.ToggleSaveReportModal,
  toggleAddEditReportModal: actionCreators.ToggleAddEditReportModal,
};

export default compose(
  localize(),
  reduxForm({
    form: "saveReportModal",
  }),
  connect(mapStateToProps, mapDispatchToProps),
)(SaveReportModal);
